"use client";

import React, { useEffect, useState } from "react";
import { Box, Flex, Container, SlideFade, VStack, Text, Skeleton, SkeletonText, Fade } from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import ClientHeader from "./ClientHeader";
import EngagementOverview from "./EngagementOverview";
import ClientDetails from "./ClientDetails";
import MeetingHistory from "./MeetingHistory";
import { ClientData as ServiceClientData } from "../../services/clientService";
import { ConversationActionItem } from "../../services/metricsService";

interface ClientProfileProps {
  clientId?: string;
  clientName?: string;
  client?: ServiceClientData;
  apiMeetings?: any[];
  conversationActionItems?: ConversationActionItem[];
  isLoadingAdditionalData?: boolean;
}

const ClientProfile: React.FC<ClientProfileProps> = ({ 
  clientId: propClientId, 
  clientName, 
  client: apiClient,
  apiMeetings = [],
  conversationActionItems = [],
  isLoadingAdditionalData = false
}) => {
  const navigate = useNavigate();
  const [isLoaded, setIsLoaded] = useState(false);
  
  // State for derived data
  const [nextSteps, setNextSteps] = useState<string[]>([]);
  const [totalMeetings, setTotalMeetings] = useState(0);
  const [lastMeetingDate, setLastMeetingDate] = useState("");
  const [pendingActions, setPendingActions] = useState(0);
  const [showContent, setShowContent] = useState(false);

  // Transform API client data to component format
  const selectedClient = apiClient ? {
    id: apiClient.id,
    name: apiClient.name,
    isActive: apiClient.status === 'Active',
    representative: apiClient.salesPerson || 'Unknown Representative',
    pendingStatuses: apiClient.isPendingActionHighlighted ? ["Pending"] : [],
    budget: apiClient.budget ? `₹${apiClient.budget.toLocaleString()}` : 'Not specified',
    decisionMakers: [apiClient.name],
    residence: apiClient.address || 'Not specified',
    email: apiClient.email || 'Not specified',
    phone: apiClient.phone || 'Not specified',
    occupation: 'Not specified',
    intent: 'Not specified',
    totalMeetings: parseInt(apiClient.totalMeetings) || 0,
    lastMeetingDate: apiClient.lastMeeting,
    pendingActions: apiClient.isPendingActionHighlighted ? 1 : 0,
    stage: 'Active',
    meetings: [],
    nextSteps: apiClient.isPendingActionHighlighted ? [apiClient.pendingAction] : []
  } : null;

  // Update derived state when props change
  useEffect(() => {
    setTotalMeetings(apiMeetings?.length || 0);
    setLastMeetingDate(apiMeetings?.[0]?.date ? new Date(apiMeetings[0].date).toLocaleDateString() : "No meetings");
    setPendingActions(conversationActionItems?.length || 0);
    
    // Generate next steps from conversation action items
    const steps = conversationActionItems?.map((action) => action.action_text) || [];
    if (apiClient?.isPendingActionHighlighted && apiClient.pendingAction) {
      steps.unshift(apiClient.pendingAction);
    }
    setNextSteps(steps);
  }, [apiMeetings, conversationActionItems, apiClient]);

  useEffect(() => {
    setIsLoaded(true); 
  }, []);

  const getAnimationDelay = (index: number) => 0.2 + (index * 0.08);

  const handleClose = () => navigate('/clients');

  useEffect(() => {
    if (isLoaded) {
      const timer = setTimeout(() => setShowContent(true), 50);
      return () => clearTimeout(timer);
    }
  }, [isLoaded, apiClient]);

  if (!isLoaded) {
    return null;
  }

  const fullClientRecord = selectedClient ? {
    ...selectedClient,
    totalMeetings: totalMeetings || selectedClient.totalMeetings,
    lastMeetingDate: lastMeetingDate || selectedClient.lastMeetingDate,
    pendingActions: pendingActions || selectedClient.pendingActions,
    nextSteps: nextSteps.length > 0 ? nextSteps : selectedClient.nextSteps,
    meetings: apiMeetings.length > 0 ? apiMeetings : selectedClient.meetings
  } : null;

  const filteredMeetings = clientName && fullClientRecord?.meetings
    ? fullClientRecord.meetings.filter(m => m.clientId && m.clientId === propClientId)
    : apiMeetings;

  return (
    <Fade in={showContent} transition={{ enter: { duration: 0.5 } }}>
      <Box as="main" bg="gray.50" minH="100vh">
        {selectedClient && (
          <ClientHeader 
            client={selectedClient}
            onClose={handleClose} 
          />
        )}
        
        <Container maxW="container.xl" mt={10} pb={10}>
          {fullClientRecord ? (
            <Flex direction={{ base: "column", lg: "row" }} gap={8}>
              <VStack spacing={8} align="stretch" flex={{ base: "1", lg: "0.65" }}>
                {isLoadingAdditionalData && filteredMeetings.length === 0 ? (
                  <Box p={6} bg="white" borderRadius="md" borderWidth="1px" borderColor="gray.200">
                    <Skeleton height="30px" width="60%" mb={4} />
                    <SkeletonText mt="4" noOfLines={4} spacing="4" />
                  </Box>
                ) : (
                  <SlideFade in={showContent} offsetY="20px" transition={{ enter: { duration: 0.5, delay: getAnimationDelay(0) } }}>
                    <EngagementOverview filteredMeetings={filteredMeetings} />
                  </SlideFade>
                )}
                {isLoadingAdditionalData && !fullClientRecord ? (
                  <Box p={6} bg="white" borderRadius="md" borderWidth="1px" borderColor="gray.200">
                    <Skeleton height="30px" width="50%" mb={4} />
                    <SkeletonText mt="4" noOfLines={6} spacing="4" />
                  </Box>
                ) : (
                  <SlideFade in={showContent} offsetY="20px" transition={{ enter: { duration: 0.5, delay: getAnimationDelay(1) } }}>
                    <ClientDetails client={fullClientRecord} /> 
                  </SlideFade>
                )}
              </VStack>
              <VStack spacing={8} align="stretch" flex={{ base: "1", lg: "0.35" }}>
                {isLoadingAdditionalData && apiMeetings.length === 0 && (!fullClientRecord || !fullClientRecord.meetings || fullClientRecord.meetings.length === 0) ? (
                  <Box p={6} bg="white" borderRadius="md" borderWidth="1px" borderColor="gray.200">
                    <Skeleton height="30px" width="70%" mb={4} />
                    <Skeleton height="40px" mb={2} />
                    <Skeleton height="40px" mb={2} />
                    <Skeleton height="40px" mb={2} />
                    <Skeleton height="40px" />
                  </Box>
                ) : (
                  <SlideFade in={showContent} offsetY="20px" transition={{ enter: { duration: 0.5, delay: getAnimationDelay(2) } }}>
                    <MeetingHistory 
                      meetings={apiMeetings.length > 0 ? apiMeetings : fullClientRecord.meetings} 
                      propClientId={propClientId}
                      clientName={clientName || fullClientRecord.name}
                    />
                  </SlideFade>
                )}
              </VStack>
            </Flex>
          ) : (
            <VStack spacing={4}>
              <Text>Client data not found.</Text>
              {isLoadingAdditionalData && <Text color="gray.500">Loading additional data...</Text>}
            </VStack>
          )}
        </Container>
      </Box>
    </Fade>
  );
};

export default ClientProfile; 